import { call as effCall, Err, Ok, resource, sleep } from "effection";
import fs from "fs";
import { createSelector } from "reselect";
import { createAssign, createTable } from "robodux";
import { call, leading, Next, put, select, spawn } from "saga-query";

import i18n from "@app/i18n";
import { encryptPayoad, ensureArray } from "@app/service/helpers";
import { api, ThunkCtx, thunks } from "@app/state/apis/api";
import { selectedGroupData } from "@app/state/units/groups";
import { appNotify } from "@app/state/units/notify";
import { selectCurrentProfTreeList } from "@app/state/units/ui/profiletree";

import { RootState } from "../rootState";
import { selectAllGroupsList } from "./groups";
import { selectMaxRole } from "./login";

import type { Operation, Result } from "effection";
import type { ApiCtx } from "saga-query";
import type { IFileItem } from "@app/types/files";

const path = "/var/lib/sws/files/COMMON";

const fileNodePattern = /^(\d+)-files$/;
const t = i18n.t.bind(i18n);

const SVN_REPO_NAME = "fsSvn";
export const fsSvnRepo = createAssign<Record<string, IFileItem>>({
  name: SVN_REPO_NAME,
  initialState: {
    svn: {
      name: "svn",
      size: 0,
      dateModified: new Date().toISOString(),
      thumbnail: "",
      isDirectory: true,
      items: [],
      roles: ["SWS"],
    },
  },
});

// export const fsSvnRepo = createTable<any>({});
const HOME_REPO_NAME = "fsHome";
export const fsHomesRepo = createTable<Record<IFileItem["name"], IFileItem>>({
  name: HOME_REPO_NAME,
  initialState: {},
});

const SWS_REMOTE_FILES = "fsSwsRemote";
export const fsSwsFiles = createAssign<Record<string, IFileItem>>({
  name: SWS_REMOTE_FILES,
  initialState: {
    swsfiles: {
      name: "swsfiles",
      size: 0,
      dateModified: new Date().toISOString(),
      thumbnail: "",
      isDirectory: true,
      items: [],
    },
  },
});

// not used - replaced by client fetch //
export const getSvnFileSys = api.get(
  "/apis/filesys",
  { saga: leading },
  function* (ctx: ApiCtx, next) {
    ctx.cache = true;
    ctx.request = ctx.req({
      url: "/swssvn/",
    });
    yield* next();
    const { data } = yield ctx.json;
    yield* put(fsSvnRepo.actions.set(data));
  }
);

export const getFsHomes = api.get(
  "/apis/filesysRoots",
  { saga: leading },
  function* (ctx: ApiCtx, next: Next) {
    ctx.cache = true;
    ctx.request = ctx.req({
      url: "/filesys/homes",
    });
    yield* next();
    const { data } = yield ctx.json;
    yield* put(fsHomesRepo.actions.set(data));
  }
);

export const getFsHomeNode = api.get<{ id: number }>(
  ["/apis/filesysRoots/:id"],
  { saga: leading },
  function* (ctx: ApiCtx, next: Next) {
    ctx.cache = true;
    ctx.request = ctx.req({
      url: `/filesys/homes/${ctx.payload}`,
    });
    yield* next();
    const { data } = yield ctx.json;
    yield* put(fsHomesRepo.actions.set(data));
  }
);

const sendNotification = function* (message: string, duration = 5000) {
  yield* spawn(appNotify.run, appNotify({ type: "error", message, duration }));
};

export const downloadFile = api.post<string[]>(
  "/apis/download/filsys",
  { saga: leading },
  function* (ctx: ApiCtx, next) {
    const { id }: { id: any } = yield* select(selectCurrentProfTreeList) as any;
    console.log("id", id);

    if (!id) return;

    const rootId = id.match(fileNodePattern)?.[1];
    if (!rootId) {
      return;
    }
    const groupData = yield* select(selectedGroupData, rootId);
    if (!groupData) {
      return;
    }
    if (!groupData.PERSEED) {
      return;
    }
    ctx.bodyType = "blob";
    const body = {
      file: ctx.payload,
    };

    ctx.request = ctx.req({
      url: `/filesys/d/${groupData.PERSEED}`,
      method: "POST",
      credentials: "include",
      body: JSON.stringify(encryptPayoad(body)),
    });
    yield* next();
    if (ctx.response?.status !== 200) {
      yield* sendNotification(t("download_error"), 3000);
      return;
    }
    const { data, ok } = yield ctx.json;
    if (!ok) {
      yield* sendNotification(t("download_error"), 3000);
      return;
    }
    try {
      const blob = new Blob([data], { type: "application/octet-stream" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;

      a.download =
        ctx.payload.length > 1
          ? "selection.zip"
          : (ctx.payload[0].split("/").pop() as string);
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e: any) {
      const errorMessage = "message" in e ? e.message : t("download_error");
      yield* sendNotification(errorMessage);
    }
  }
);

export const startingScript = api.post<{ fileName: string; timespan: string }>(
  "/apis/startScript",
  { saga: leading },
  function* (ctx: ApiCtx, next) {
    ctx.bodyType = "blob";
    const body = {
      fileName: ctx.payload.fileName,
      timespan: ctx.payload.timespan,
    };

    ctx.request = ctx.req({
      url: `/filesys/startScript`,
      method: "POST",
      credentials: "include",
      body: JSON.stringify(body),
    });
    yield* next();
    if (ctx.response?.status !== 200) {
      yield* sendNotification(t("download_error"), 3000);
      return;
    }
    const { data, ok } = yield ctx.json;
    if (!data) {
      yield* sendNotification(t("download_error"), 3000);
      return;
    }
    try {
      const blob = new Blob([data], { type: "application/octet-stream" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "concatenation.zip"; // Set download file name directly
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e: any) {
      const errorMessage = "message" in e ? e.message : t("download_error");
      yield* sendNotification(errorMessage);
    }
  }
);

export const loadFileSys = thunks.create(
  "/thunks/fileSys/load",
  function* (ctx: ThunkCtx, next: Next) {
    yield* call(getFsHomes.run, getFsHomes());
    yield* next();
  }
);

export const fileResource = (path: string[]) =>
  resource(function* (provide): Operation<any> {
    console.log("path", path);
    yield* sleep(3000);
    const result = Ok(path);
    return yield* provide(result);
  });

export const getResourceTest = (param: any): Operation<Result<string>> =>
  resource(function* (provide) {
    let result = Ok("wait");
    yield* sleep(3000);
    result = Ok(param);

    return yield* provide(result);
  });

function createParameterSelector(selector: any) {
  return (_: any, params: any) => selector(params);
}
const getRoots = createParameterSelector((params: any) => params?.roots || []);

const svnSelectors = (state: RootState) => state[SVN_REPO_NAME];

export const selectSVNFileSys = createSelector(svnSelectors, (s) => s);

const homeSelectors = fsHomesRepo.getSelectors(
  (state: RootState) => state[HOME_REPO_NAME]
);

export const selectHomeFileSys = createSelector(
  homeSelectors.selectTable,
  (s) => s
);

export const selectFileSystemRoots = createSelector(
  selectSVNFileSys,
  selectHomeFileSys,
  selectAllGroupsList,
  selectMaxRole,
  getRoots,
  (svn, homes, groups, maxRole, roots_) => {
    const roots = ensureArray(roots_);
    const filesys: IFileItem[] = [];

    for (const root of roots) {
      // is a home root of format 123-files //
      const rootId = root.match(fileNodePattern)?.[1];
      if (rootId) {
        const homeKey =
          `/home/${groups[rootId]?.PERSEED}` as keyof typeof homes;
        if (homeKey) {
          //fixme:
          const homeItem = homes[homeKey] as any as IFileItem[] | undefined;
          if (homeItem) {
            filesys.push({
              name: "setup",
              size: 0,
              dateModified: new Date().toISOString(),
              thumbnail: "",
              isDirectory: true,
              items: homeItem as IFileItem[],
            });
          }
        }
      }
      // is a svn node
      if (root === "svn" && maxRole === "SWS") {
        const svnNode = {
          name: "svn",
          size: 0,
          dateModified: new Date().toISOString(),
          thumbnail: "",
          isDirectory: true,
          items: svn,
        };

        // Object.assign(filesys, { svn: svnNode });
        filesys.push(svnNode);
      }
      // is our tools root
      if (root === "COMMON" && maxRole === "SWS") {
        const commonItem = homes?.["/home/COMMON"] as any as
          | IFileItem[]
          | undefined;
          
        const toolsNode = {
          name: "common",
          size: 0,
          dateModified: new Date().toISOString(),
          thumbnail: "",
          isDirectory: true,
          items: commonItem as IFileItem[],
        };
        filesys.push(toolsNode);
      }
    }
    return filesys;
  }
);
